import {useAui} from '@assistant-ui/react';
import {useEffect, useRef} from 'react';

import {
  useActiveThread,
  useActiveThreadLoadState,
} from '../../agentChat/store/context';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

/**
 * WBAgentSessionRestore reopens the persisted committed thread on a full page
 * load and keeps `restoreThreadId` synced to the displayed committed thread.
 * Renders nothing.
 *
 * NOTE: A component, not a hook, so it can be mounted only when the agent is
 * available; its `useAui()` call would throw without AuiProvider.
 *
 * NOTE: Restoring an archived or deleted thread opens to the not-found state
 * (the load returns no turns); the sync effect then clears `restoreThreadId`,
 * so the next load stays closed.
 */
export const WBAgentSessionRestore = () => {
  const aui = useAui();
  const activeThread = useActiveThread();
  const open = useWBAgentWindowStore(s => s.open);
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const setRestoreThreadId = useWBAgentWindowStore(s => s.setRestoreThreadId);

  // Capture the persisted target at mount for the one-shot restore below.
  const targetRef = useRef(useWBAgentWindowStore.getState().restoreThreadId);

  // Restore once on mount. On failure, 'switchToThread' dispatches
  // 'failLoadThread', so the open chat shows the existing error state.
  useEffect(() => {
    const target = targetRef.current;
    if (target != null) {
      setOpen(true);
      void aui.threads().switchToThread(target);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rootTurnId = activeThread?.rootTurnId ?? null;
  const {isLoading, failure} = useActiveThreadLoadState();

  // Sync 'restoreThreadId' to the shown committed thread, skipping the mount
  // run. A not-found load leaves no active thread, so 'rootTurnId' is null and
  // the target clears instead of re-persisting into a loop.
  const isHydratedRef = useRef(false);
  useEffect(() => {
    if (!isHydratedRef.current) {
      isHydratedRef.current = true;
      return;
    }

    // NOTE: While a switch loads, the thread isn't in the store yet, so a write
    // here would persist a transient null and lose the restore target.
    if (isLoading) {
      return;
    }

    // NOTE: Keep the target after a failed load so a refresh retries it; a
    // not-found load falls through and clears. An explicit close clears below.
    if (open && failure?.kind === 'error') {
      return;
    }

    setRestoreThreadId(open && rootTurnId != null ? rootTurnId : null);
  }, [open, failure, rootTurnId, isLoading, setRestoreThreadId]);

  return null;
};
